import { formatDateDayMonthYear } from "@utils/dateHelpers";
import { helperFetch } from "@utils/fetchHelpers";
import { GetAllFileListItems } from "@utils/fileListBlockHelpers";
import {
  InterceptContactBlock,
  InterceptFaqListResult,
  InterceptFaqPage,
  InterceptFileBlock,
  InterceptImageBlock,
  InterceptInsurancePickerBlock,
  InterceptLinkBlock,
  InterceptLinkObjectsAndSetUrl,
  InterceptLinksCollection,
  InterceptOrderBlock,
  InterceptPageBlock,
  InterceptPagesAndSetImages,
  InterceptPagesAndSetUrlsAndNames,
  InterceptPersonalizedClaimLinksCollection,
  InterceptPublishDates,
  InterceptQuoteBlock,
  InterceptRichText,
  InterceptTeaserBlock,
  InterceptTeaserListBlock,
  InterceptVideoBlock,
} from "@utils/interceptors";
import { getContentTypeQuery, getPageType } from "queries";

import { GET_LINKED_FAQS_BY_SLUG_METHOD } from "queries/global/getLinkedFaqsStructure.query";
import { GET_FAQPAGE_BY_SLUG_METHOD } from "queries/pages/getFaqPageBySlug.query";
import { GET_PAGE_BY_SLUG_METHOD } from "queries/pages/getPageBySlug.query";
import { getResultForFAQListBlock } from "services/FAQListService";
import { getResultForListBlock } from "services/ListService";
import { UrlStructure } from "types/UrlStructure";

const CONTENTFUL_SPACE_ID = process.env.CONTENTFUL_SPACE_ID;
const CONTENTFUL_ACCESS_TOKEN = process.env.CONTENTFUL_ACCESS_TOKEN;
const CONTENTFUL_ENVIRONMENT = process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT;
const CONTENTFUL_PREVIEW_ACCESS_TOKEN = process.env.CONTENTFUL_PREVIEW_ACCESS_TOKEN;

const keyOf = (obj: any) => Object.keys(obj ? obj : {});

export const pageResolver = (fetchResponse) => {
  return fetchResponse?.data?.pageCollection?.items?.[0];
};

export const fetchCMSData = async (query, preview: boolean) => {
  const resolvedToken = preview ? CONTENTFUL_PREVIEW_ACCESS_TOKEN : CONTENTFUL_ACCESS_TOKEN;

  if (!CONTENTFUL_SPACE_ID || !CONTENTFUL_ENVIRONMENT || !resolvedToken) {
    throw new Error(
      "Missing required Contentful server environment variables (CONTENTFUL_SPACE_ID, NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT, and access token).",
    );
  }

  const url = preview
    ? `https://graphql.contentful.com/content/v1/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}?access_token=${CONTENTFUL_PREVIEW_ACCESS_TOKEN}`
    : `https://graphql.contentful.com/content/v1/spaces/${CONTENTFUL_SPACE_ID}/environments/${CONTENTFUL_ENVIRONMENT}`;

  const res = await helperFetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${resolvedToken}`,
    },
    body: JSON.stringify({ query }),
  });

  const { data, errors } = await res.json();
  //Temporarily disabled 'cause we have tons of errors and don't want to spam the logs
  // if (errors) {
  //   for (const error of errors) {
  //     console.debug(`Error from Contentful: ${JSON.stringify(error)}`);
  //   }
  // }

  return { data, errors };
};

export const getBlockTypes = async (
  data,
  urlStructure: UrlStructure,
  paginationPage: number,
  preview: boolean,
) => {
  for (const key of keyOf(data)) {
    InterceptRichText(key, data, urlStructure);
    //Intercept linksCollection and set the url from our Url-Structure
    InterceptLinksCollection(key, data, urlStructure);
    //Intercept personalizedClaimLinksCollection and set the url from our Url-Structure
    InterceptPersonalizedClaimLinksCollection(key, data, urlStructure);
    InterceptFaqListResult(key, data, urlStructure);
    //Do not include these arrays in blockgeneration.
    const excludedArraysInBlockGeneration = [
      "personalizedClaimLinksCollection",
      "linksCollection",
      "manualPostsCollection",
      "availableForAudiencesCollection",
      "mainAreaOrderCollection",
      "relatedEventsCollection",
      "listresult",
      "faqListResult",
      "questionAreaCollection",
      "listItemsCollection",
    ];

    //Check if array has items/blocks and generate them unless they are excluded in blockgeneration.
    if (data[key]?.items?.length && !excludedArraysInBlockGeneration.includes(key)) {
      const promises: Promise<any>[] = (data[key]?.items || []).map(async (item: any) => {
        // Item here is sometimes null? Just bail early to avoid errors further down.
        if (!item) {
          return null;
        }

        //Get the graphQL-query for current block (item)
        const blockQuery = getContentTypeQuery(item?.__typename, item?.sys?.id, preview);

        //if no query is present for the blocktype, then skip the this block and move on.
        //Example when this can happen: If we have a model present in production which we
        //work on locally and it's added to a contentarea in contentful
        if (!blockQuery) {
          return null;
        }
        //Fetch the block-data from Contentful
        const res = await fetchCMSData(blockQuery, preview);

        //if block does not exist in this specific query
        if (!item?.sys?.id) {
          return null;
        }

        if (res?.data) {
          const blockName = Object.keys(res?.data)[0];
          res.data[blockName].id = item?.sys?.id;
        }

        //Intercept BlockData from contentful and manipulate the data.
        InterceptBlockData(res, item?.__typename, urlStructure);

        // START - Get results for lists
        // If block is any type of list where we need to fetch a result and add that to the final result
        // we will do that here
        if (res && res?.data && item?.__typename?.toLowerCase() === "listblock") {
          const listBlock = res?.data;
          const result = await getResultForListBlock(
            listBlock,
            paginationPage,
            preview,
            urlStructure,
          );
          listBlock.listBlock.listresult = result;
          return listBlock;
        } else if (res && res?.data && item?.__typename?.toLowerCase() === "filelistblock") {
          const fileListBlock = res?.data;
          const fileListBlockResult = await GetAllFileListItems(
            fileListBlock?.fileListBlock,
            urlStructure,
            paginationPage,
            preview,
          );
          fileListBlock.fileListBlock.listresult = fileListBlockResult;
          return fileListBlock;
        } else if (res && res?.data && item?.__typename?.toLowerCase() === "faqlistblock") {
          const faqListBlock = res?.data;
          const faqListBlockResult = await getResultForFAQListBlock(faqListBlock, preview);
          faqListBlock.faqListBlock.faqListResult = faqListBlockResult;

          return faqListBlock;
        } else if (res && res.data) {
          return res.data;
        }

        return null;
      });

      const items = (await Promise.all(promises)).filter((item) => item !== null);

      await Promise.all(
        items.map((item) =>
          getBlockTypes(item[keyOf(item)[0]], urlStructure, paginationPage, preview),
        ),
      );

      data[key].items = items;
    } else if (
      data[key]?.__typename &&
      data[key]?.sys?.id &&
      (key === "fallbackVariation" || key === "personalizedMessageFallback")
    ) {
      const res = await fetchCMSData(
        getContentTypeQuery(data[key]?.__typename, data[key]?.sys?.id, preview),
        preview,
      );

      if (res?.data) {
        const blockName = Object.keys(res?.data)[0];
        res.data[blockName].id = data[key]?.sys?.id;
      }

      //Intercept BlockData and manipulate the data.
      InterceptBlockData(res, data[key]?.__typename, urlStructure);

      const items: any[] = [];
      if (res?.data) {
        items.push(res.data);
      }

      await Promise.all(
        items.map((item) =>
          getBlockTypes(item[keyOf(item)[0]], urlStructure, paginationPage, preview),
        ),
      );

      data[key] = items[0];
    }
  }
  if (data?.relatedContentArea?.linkOnFaqList) {
    InterceptFaqPage(data?.relatedContentArea?.linkOnFaqList, urlStructure);
  }
  return data;
};

// Lightweight metadata fetch: runs only the slug query (which already returns
// the full `seo` object) and skips page-type/block/FAQ resolving. Keeps
// generateMetadata fast so <title>/robots/canonical land in <head>.
export const fetchPageSeoBySlug = async (slug: string, preview: boolean): Promise<any> => {
  const entries: any = await fetchCMSData(GET_PAGE_BY_SLUG_METHOD(slug, preview), preview);

  if (entries?.data && !entries?.data?.pageCollection?.items?.length) {
    return 404;
  }

  return { slug: pageResolver(entries) };
};

export const fetchPageBySlug = async (
  urlStructure: UrlStructure,
  slug: string,
  paginationPage: number,
  preview: boolean,
) => {
  const page: any = {
    slug: {
      data: {
        items: [],
      },
    },
  };

  // Hämtar root sidan baserad på slug och mappar till page.slug
  const query = GET_PAGE_BY_SLUG_METHOD(slug, preview);

  const entries: any = await fetchCMSData(query, preview);

  if (entries?.data && !entries?.data?.pageCollection?.items?.length) {
    return 404;
  } else {
    page.slug = pageResolver(entries);

    const pageTypeQuery = getPageType(
      page?.slug?.content?.__typename,
      page?.slug?.content?.sys?.id,
      preview,
    );

    // Här hämtar jag sidtypen för slug-sidan
    const pageEntries: any = await fetchCMSData(pageTypeQuery, preview);

    //Intercept eventPages and set registrationLink
    InterceptPublishDates(pageEntries?.data);
    InterceptPagesAndSetUrlsAndNames(pageEntries?.data, page?.slug?.name, urlStructure);
    InterceptPagesAndSetImages(pageEntries?.data[keyOf(pageEntries?.data)[0]]);

    const [pageSlugData, faqs] = await Promise.all([
      getBlockTypes(
        pageEntries?.data[keyOf(pageEntries?.data)[0]],
        urlStructure,
        paginationPage,
        preview,
      ),
      fetchLinkedFaqsBySlug(slug, preview),
    ]);

    page.slug.data = pageSlugData;

    if (faqs && faqs.length > 1) {
      faqs?.map((faq) => {
        InterceptRichText("answer", faq, urlStructure);
      });
      page.slug.seo.linkedFaqs = faqs;
    }

    //ADD latestReviewDate to all pagetypes
    if (page?.slug?.latestReviewDate) {
      page.slug.data.latestReviewDate = [formatDateDayMonthYear(page?.slug?.latestReviewDate)];
    }
    //ADD pageUpdateDate to all pagetypes
    if (page?.slug?.pageUpdateDate) {
      page.slug.data.pageUpdateDate = [formatDateDayMonthYear(page?.slug?.pageUpdateDate)];
    }
    if (page?.slug?.authorName) {
      page.slug.data.authorName = [page?.slug?.authorName];
    }
    if (page?.slug?.reviewName) {
      page.slug.data.reviewName = [page?.slug?.reviewName];
    }
    if (page?.slug?.reviewRole) {
      page.slug.data.reviewRole = [page?.slug?.reviewRole];
    }
    if (page?.slug?.pageLanguage) {
      page.slug.data.pageLanguage = page?.slug?.pageLanguage;
    }
    return page;
  }
};

const InterceptBlockData = (res, typeName, urlStructure) => {
  InterceptFileBlock(res, typeName);
  InterceptOrderBlock(res, typeName);
  InterceptLinkObjectsAndSetUrl(res, urlStructure);
  InterceptVideoBlock(res, typeName, urlStructure);
  InterceptQuoteBlock(res, typeName);
  InterceptImageBlock(res, typeName);
  InterceptTeaserBlock(res, typeName, urlStructure);
  InterceptPageBlock(res, typeName, urlStructure);
  InterceptLinkBlock(res, typeName, urlStructure);
  InterceptContactBlock(res, typeName, urlStructure);
  InterceptTeaserListBlock(res, typeName, urlStructure);
  InterceptInsurancePickerBlock(res, typeName, urlStructure);
};

export const fetchLinkedFaqsBySlug = async (
  slug: string,
  preview: boolean,
): Promise<Array<any>> => {
  // Hämtar root sidan baserad på slug och mappar till page.slug
  const faqs = await getAllLinkedFaqs(slug, preview);
  return faqs?.length ? faqs : [];
};

const getAllLinkedFaqs = async (slug: string, preview: boolean) => {
  const faqs: any[] = [];

  const getAllFAQ = async (slug: string, skip: number): Promise<Array<any>> => {
    const { data } = await fetchCMSData(
      GET_LINKED_FAQS_BY_SLUG_METHOD(slug, skip, preview),
      preview,
    );

    for (const i of data?.faqCollection?.items) {
      faqs.push(i);
    }
    if (data?.faqCollection?.total > faqs.length) {
      return await getAllFAQ(slug, faqs.length);
    }
    return faqs;
  };
  const allFaqs = await getAllFAQ(slug, 0);
  return allFaqs as Array<any>;
};

export const fetchFaqPageBySlug = async (
  urlStructure: UrlStructure,
  slug: string,
  paginationPage: number,
  preview: boolean,
) => {
  // Hämtar root sidan baserad på slug och mappar till page.slug
  const entries: any = await fetchCMSData(GET_FAQPAGE_BY_SLUG_METHOD(slug, preview), preview);

  if (entries?.data && !entries?.data?.faqCollection?.items?.length) {
    return 404;
  } else {
    const pageWithBlocks = await getBlockTypes(
      entries?.data?.faqCollection?.items?.[0],
      urlStructure,
      paginationPage,
      preview,
    );

    return {
      slug: {
        data: pageWithBlocks,
        seo: { ...pageWithBlocks?.seo },
        content: {
          __typename: "faq",
        },
      },
    };
  }
};
